import { createPasswordPolicy, PasswordPolicy } from '@acme/b2b/security';
import { passwordValidatorFactory } from '@acme/b2b/validators';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { ApiService } from '@b2b/common/services/api/types';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { TODO } from '@b2b/types/core';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { ILogService, IScope } from 'angular';

OnboardController.$inject = [
  '$scope',
  '$state',
  '$log',
  'ApiService',
  'ValidateService',
  'AuthService',
  'NoticeFactory',
  'ThemesHelperService',
];

function OnboardController(
  $scope: IScope & { onboard: TODO },
  $state: StateService,
  $log: ILogService,
  ApiService: ApiService,
  ValidateService: TODO,
  AuthService: TODO,
  NoticeFactory: NoticeFactory,
  ThemesHelperService: ThemesHelperService
) {
  const ctrl = {
    notice: new NoticeFactory(),

    model: {
      tenant: {},
      user: {},
      tenantId: '',
      userId: '',
      confirmPassword: '',
    } as TODO,

    countries: [
      { name: 'United States', alpha2Code: 'US' },
      { name: 'Canada', alpha2Code: 'CA' },
      { name: 'New Zealand', alpha2Code: 'NZ' },
      { name: 'Other', alpha2Code: 'OTHER' },
    ] as TODO[],

    selectedCountry: {} as TODO,

    isAdmin: false,
    isTentantSubmitted: false,
    isUserSubmitted: false,
    step: 1,
    totalSteps: 2,
    showErrors: false,
    errors: [],

    passwordPolicy: null as PasswordPolicy | null,
    confirmPasswordError: '',
    userPasswordError: '',

    init: function () {
      const user = AuthService.getCookie('user');
      ctrl.setDefaultCountry(ctrl.countries[0]);
      if (user != null) {
        ctrl.model.tenantId = user.tenantId;
        ctrl.model.userId = user.id;
        ctrl.isAdmin = AuthService.hasRole('admin');

        if (ctrl.isAdmin) {
          ctrl.getTenant();
        } else {
          ctrl.step = 2;
        }

        ctrl.getUser();
        ctrl.loadTheme();
      }
      ctrl.notice = new NoticeFactory();
    },

    stepBack: function () {
      ctrl.step -= 1;
      ctrl.isTentantSubmitted = false;
      ctrl.isUserSubmitted = false;
      ctrl.notice.hideMessages();
    },

    isStep: function (step) {
      return ctrl.step === step;
    },
    isValidEmail: function () {
      if (ctrl.isTentantSubmitted) {
        return ValidateService.validate([{ email: ctrl.model.user.email }]);
      }

      return true;
    },

    isValidCompanyName: function () {
      if (ctrl.isTentantSubmitted) {
        return ctrl.isValidLen(ctrl.model.tenant.name + '');
      }

      return true;
    },

    isValidAddress: function () {
      if (ctrl.isTentantSubmitted) {
        return ctrl.isValidLen(ctrl.model.tenant.streetAddress1);
      }

      return true;
    },

    isValidAddress2: function () {
      if (ctrl.isTentantSubmitted && ctrl.model.tenant.streetAddress2) {
        return ctrl.isValidLen(ctrl.model.tenant.streetAddress2);
      }

      return true;
    },

    isValidCity: function () {
      if (ctrl.isTentantSubmitted) {
        return ctrl.isValidLen(ctrl.model.tenant.city);
      }

      return true;
    },

    isValidState: function () {
      if (ctrl.isTentantSubmitted) {
        return ctrl.isValidLen(ctrl.model.tenant.state);
      }

      return true;
    },

    isValidZip: function () {
      if (ctrl.isTentantSubmitted) {
        return ctrl.isValidLen(ctrl.model.tenant.zipCode);
      }

      return true;
    },

    isValidCountry: function () {
      if (ctrl.isTentantSubmitted) {
        return ctrl.isValidLen(ctrl.model.tenant.country);
      }

      return true;
    },

    isValidPhone: function () {
      if (ctrl.isTentantSubmitted) {
        return ValidateService.validate([
          { len: { value: ctrl.model.tenant.phoneNumber } },
          { phone: ctrl.model.tenant.phoneNumber },
        ]);
      }

      return true;
    },

    isValidUserPhone: function () {
      if (ctrl.isUserSubmitted) {
        return ValidateService.validate([
          { len: { value: ctrl.model.user.phoneNumber } },
          { phone: ctrl.model.user.phoneNumber },
        ]);
      }

      return true;
    },

    isValidFirstName: function () {
      if (ctrl.isUserSubmitted) {
        return ctrl.isValidName(ctrl.model.user.firstName);
      }

      return true;
    },

    isValidLastName: function () {
      if (ctrl.isUserSubmitted) {
        return ctrl.isValidName(ctrl.model.user.lastName);
      }

      return true;
    },

    isValidLen: function (value) {
      return ValidateService.validate([{ len: { value: value } }]);
    },

    isValidConfirmPassword: function (): boolean {
      if (ctrl.isUserSubmitted) {
        const valid = ValidateService.validate([
          {
            field: 'confirmPassword',
            equal: {
              message: 'Your passwords must be the same.',
              test: [ctrl.model.user.password, ctrl.model.confirmPassword],
            },
          },
        ]);

        ctrl.confirmPasswordError = valid ? '' : ValidateService.errors[0].message;

        return valid;
      }

      return true;
    },

    isValidUserPassword: function () {
      const passwordPolicy = createPasswordPolicy(ctrl.passwordPolicy ?? {});
      const passwordValidator = passwordValidatorFactory(passwordPolicy);

      if (ctrl.isUserSubmitted) {
        const valid = ValidateService.validate([
          {
            field: 'userPassword',
            predicate: {
              test: { predicateFn: () => passwordValidator(ctrl.model.user.password) == null },
              message: 'Please enter a valid password',
            },
          },
        ]);

        ctrl.userPasswordError = valid ? '' : ValidateService.errors[0].message;

        return valid;
      }

      return true;
    },

    isValidName: function (name) {
      if (ctrl.isUserSubmitted) {
        return ValidateService.validate([{ name: name }]);
      }

      return true;
    },

    isValidStep: function (step) {
      let r = false;

      switch (step) {
        case 1:
          r = ValidateService.validate([
            { len: { value: ctrl.model.tenant.zipCode } },
            { len: { value: ctrl.model.tenant.name } },
            { len: { value: ctrl.model.tenant.streetAddress1 } },
            { len: { value: ctrl.model.tenant.country } },
            { len: { value: ctrl.model.tenant.city } },
          ]);
          break;
        case 2:
          r = ValidateService.validate([
            { email: ctrl.model.user.email },
            { password: ctrl.model.user.password },
            { equal: [ctrl.model.user.password, ctrl.model.confirmPassword] },
            { name: ctrl.model.user.firstName },
            { name: ctrl.model.user.lastName },
            { phone: ctrl.model.user.phoneNumber },
          ]);
        /* falls through */
        default:
          break;
      }
      return r;
    },

    getTenant: function () {
      ctrl.showErrors = false;

      // TODO: check if this actually needs the tenantID
      ApiService.tenant
        .view(ctrl.model.tenantId)
        .then((response) => ctrl.success.getTenant(response))
        .catch((response) => ctrl.error.getTenant(response));
    },

    getUser: function () {
      ctrl.showErrors = false;
      ApiService.user
        .view(ctrl.model.userId)
        .then((response) => ctrl.success.getUser(response))
        .catch((response) => ctrl.error.getUser(response));
    },

    loadTheme: function () {
      return ApiService.themes
        .detail(false)
        .then((response) => {
          ThemesHelperService.setModel(response.data.payload);

          ctrl.passwordPolicy = createPasswordPolicy({
            passwordSecurityPolicyEnabled: response.data.payload.company.passwordSecurityPolicyEnabled,
            passwordMinimumLength: response.data.payload.company.passwordMinimumLength,
            passwordUseMixedCase: response.data.payload.company.passwordUseMixedCase,
            passwordMinimumDigits: response.data.payload.company.passwordMinimumDigits,
            passwordMinimumSymbols: response.data.payload.company.passwordMinimumSymbols,
          });
        })
        .catch(() => {
          // noop
        });
    },

    activateTenant: function () {
      ctrl.isTentantSubmitted = true;

      if (ctrl.isValidStep(1)) {
        console.log('OnboardController::activateTenant');
        ctrl.notice.hideMessages();
        ApiService.tenant
          .activate(ctrl.model.tenant)
          .then((response) => ctrl.success.activateTenant(response))
          .catch((response) => ctrl.error.activateTenant(response));
      } else {
        ctrl.notice.showErrorMessages([{ message: 'Please correct the errors below.' }]);
      }
    },

    updateUser: function () {
      ctrl.isUserSubmitted = true;

      if (ctrl.isValidStep(2)) {
        ctrl.notice.hideMessages();
        ApiService.user
          .update(ctrl.model.user)
          .then((response) => ctrl.success.updateUser(response))
          .catch((response) => ctrl.error.updateUser(response));
      } else {
        ctrl.notice.showErrorMessages([{ message: 'Please correct the errors below.' }]);
      }
    },

    complete: function (step) {
      switch (step) {
        case 1:
          if (ctrl.isAdmin) {
            ctrl.activateTenant();
            console.log('OnboardController::complete', step);
          }
        /* falls through */
        default:
          break;
        case 2:
          ctrl.updateUser();
          break;
      }
    },

    getCountries: function () {
      ApiService.form.avs.countries().then(ctrl.success.getCountries, ctrl.error.getCountries);
    },

    findCountry: function (name) {
      let r: { country: TODO; index: number } | false = false;

      for (let i = 0, ii = ctrl.countries.length; i < ii; i++) {
        if (
          ctrl.countries[i].name === name ||
          ctrl.countries[i].alpha2Code === name ||
          ctrl.countries[i].alpha3Code === name
        ) {
          r = {
            country: ctrl.countries[i],
            index: i,
          };
          break;
        }
      }

      return r;
    },

    setDefaultCountry: function (country) {
      ctrl.model.tenant.country = country.name;
      ctrl.selectedCountry = country;
    },

    onSelectedCountryUpdate: function (e, data) {
      if (ctrl.selectedCountry) {
        ctrl.model.tenant.country = ctrl.selectedCountry.name;
      } else {
        ctrl.model.tenant.country = '';
      }
    },

    success: {
      getTenant: function (response) {
        ctrl.model.tenant = response.data.tenant;
        ctrl.getCountries();
      },
      getUser: function (response) {
        console.log('getUser', response.data.user);
        ctrl.model.user = response.data.user;
      },
      activateTenant: function (response) {
        ctrl.step = 2;
      },
      updateUser: function (response) {
        const path = AuthService.getLoggedInPaths();
        if (path !== false) {
          $state.go(path.state);
        }
      },
      getCountries: function (response) {
        let found: TODO;

        ctrl.countries = response.data.payload;
        if (!ctrl.model.tenant.country) {
          ctrl.setDefaultCountry(ctrl.countries[0]);
        } else {
          found = ctrl.findCountry(ctrl.model.tenant.country);
          if (found) {
            ctrl.selectedCountry = found.country;
            ctrl.model.tenant.country = found.country.name;
          }
        }
      },
    },

    error: {
      getTenant: function (response) {
        ctrl.notice.showErrorMessages([{ message: 'Could not find company: ' + response.data.error }]);
      },
      getUser: function (response) {
        ctrl.notice.showErrorMessages([{ message: 'Could not find user: ' + response.data.error }]);
      },
      activateTenant: function (response) {
        ctrl.notice.showErrorMessages([{ message: 'Unable to activate company: ' + response.data.error }]);
      },
      updateUser: function (response) {
        ctrl.notice.showErrorMessages([{ message: 'Unable to update user: ' + response.data.error }]);
      },
      getCountries: function (response) {},
    },
  };

  $scope.onboard = ctrl;
  ctrl.init();
}

export const OnboardControllerModule = angular
  .module('acme.view.onboard.controllers', [
    uiRouter,
    'acme.common.service.api',
    'acme.common.service.validate',
    'acme.common.service.auth',
    'acme.common.factory.notice',
    'acme.common.service.themesHelper',
  ])
  .controller('OnboardController', OnboardController).name;
